import React from 'react';
import { Trans } from 'react-i18next';

import type { PredicateFilter, PredicatePreviewerFunc } from '@common/components/predicates-filter/predicates-filter';
import { optionsPreviewer } from '@common/components/predicates-filter/predicates-filter';
import { PredicateSelectorMultiSelect } from '@common/components/predicates-filter/filter-multi-select';
import { PredicateSelectorTextContains } from '@common/components/predicates-filter/filter-text-contains';
import { PredicateSelectorBoolean } from '@common/components/predicates-filter/filter-boolean';
import { EPredicateOperators } from '@common/definitions';
import i18n from '../../../i18n';

import {
  buildCustomUserPropertyAttribute,
  normalizePredicateBooleanValue,
} from './custom-user-property-predicate';
import { getCustomUserPropertyTypeIcon } from './get-type-icon';
import {
  type CustomUserProperty,
  ECustomUserPropertyType,
} from '../types';

export const CUSTOM_USER_PROPERTY_FILTER_CATEGORY = 'user_property';

export type CustomUserPropertyPredicateFilter = PredicateFilter & {
  value: string;
  label: string;
  propertyId: string;
  propertyType: CustomUserProperty['type'];
  isCustomUserProperty: true;
};

const textContainsPreviewer: PredicatePreviewerFunc<{ label: string }> = ({ value }) => {
  if (typeof value !== 'string' || !value.trim()) {
    return (
      <span>
        <Trans i18nKey="common:predicates_filter_in" />
        {' '}
        <Trans i18nKey="common:predicates_filter_any" />
      </span>
    );
  }

  return <span>{value}</span>;
};

const booleanPreviewer: PredicatePreviewerFunc = ({ value }) => {
  const normalizedValue = normalizePredicateBooleanValue(value);

  if (normalizedValue === null) {
    return (
      <span>
        <Trans i18nKey="common:predicates_filter_is" />
        {' '}
        <Trans i18nKey="common:predicates_filter_any" />
      </span>
    );
  }

  return (
    <span>
      <Trans i18nKey="common:predicates_filter_is" />
      {' '}
      {normalizedValue === true
        ? i18n.t('common:predicates_filter_user_property_yes')
        : i18n.t('common:predicates_filter_user_property_no')}
    </span>
  );
};

function mapDropdownOptions(property: CustomUserProperty) {
  return (property.options ?? []).map((option) => ({
    id: option.id,
    name: option.value,
  }));
}

export function buildCustomUserPropertyPredicateFilter(
  property: CustomUserProperty
): CustomUserPropertyPredicateFilter {
  const attribute = buildCustomUserPropertyAttribute(property.id);
  const base = {
    value: attribute,
    label: property.name,
    propertyId: property.id,
    propertyType: property.type,
    isCustomUserProperty: true as const,
    icon: getCustomUserPropertyTypeIcon(property.type),
    context: 'user_property',
    category: CUSTOM_USER_PROPERTY_FILTER_CATEGORY,
  };

  if (property.type === ECustomUserPropertyType.OPEN_TEXT) {
    return {
      ...base,
      defaultOperator: EPredicateOperators.CONTAINS,
      defaultValue: '',
      selector: PredicateSelectorTextContains,
      previewer: textContainsPreviewer as PredicatePreviewerFunc,
    };
  }

  if (property.type === ECustomUserPropertyType.BOOLEAN) {
    return {
      ...base,
      defaultOperator: EPredicateOperators.EQ,
      defaultValue: true,
      selector: PredicateSelectorBoolean,
      previewer: booleanPreviewer,
    };
  }

  return {
    ...base,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer as PredicatePreviewerFunc,
    valueKey: 'id',
    labelKey: 'name',
    getData: () => mapDropdownOptions(property),
  };
}

export function buildCustomUserPropertyPredicateFilters(
  properties: CustomUserProperty[]
): Record<string, CustomUserPropertyPredicateFilter> {
  return properties.reduce<Record<string, CustomUserPropertyPredicateFilter>>((acc, property) => {
    const filter = buildCustomUserPropertyPredicateFilter(property);
    acc[filter.value] = filter;
    return acc;
  }, {});
}

export function getCustomUserPropertyFilterOptions(
  filter: CustomUserPropertyPredicateFilter,
  property: CustomUserProperty | undefined
) {
  if (filter.propertyType !== ECustomUserPropertyType.DROPDOWN || !property) {
    return [];
  }

  return mapDropdownOptions(property);
}
